<template>
    <form
        class="space-y-6"
        @submit="handleSubmit"
    >
        <div class="text-lg font-medium text-gray-600">
            {{ t('signInFlow.noAccountFound') }}
        </div>

        <i18n-t
            keypath="signInFlow.guardianNote"
            tag="div"
            class="text-lg font-medium"
        >
            <template #guardianLabel>
                <span class="text-red-600 font-semibold">
                    {{ t('signInFlow.guardianLabel') }}
                </span>
            </template>
        </i18n-t>

        <div ref="firstNameRef">
            <div>{{ t('signInFlow.yourName') }}</div>
            <div class="grid grid-cols-3 gap-3">
                <div>
                    <input-text
                        id="first_name"
                        v-model="firstName"
                        type="text"
                        class="w-full"
                        :disabled="isPending"
                        :placeholder="t('customerInformation.firstName')"
                        required
                    />
                    <small
                        v-if="showErrors && firstNameInvalidMessage !== null"
                        id="firstname-help"
                        class="text-red-600"
                        >{{ t('customerInformation.firstNameRequired') }}</small
                    >
                </div>

                <div>
                    <input-text
                        id="middle_name"
                        v-model="middleName"
                        type="text"
                        class="w-full"
                        :disabled="isPending"
                        :placeholder="
                            t('customerInformation.middleNameOptional')
                        "
                    />
                </div>

                <div>
                    <input-text
                        id="last_name"
                        v-model="lastName"
                        type="text"
                        class="w-full"
                        :disabled="isPending"
                        :placeholder="t('customerInformation.lastName')"
                        required
                    />
                    <small
                        v-if="showErrors && lastNameInvalidMessage !== null"
                        id="lastname-help"
                        class="text-red-600"
                        >{{
                            t('customerInformation.twoOrMoreCharactersRequired')
                        }}</small
                    >
                </div>
            </div>
        </div>

        <div>
            <div class="flex flex-col gap-6">
                <div v-if="useDateOfBirth">
                    <div>{{ t('signInFlow.yourDateOfBirth') }}</div>
                    <dropdown-date-input
                        v-model:date="dateOfBirth"
                        :show-errors="showErrors"
                        :disabled="isPending"
                        :invalid="
                            dateOfBirthInvalidMessage !== null &&
                            dateOfBirthInvalidMessage !== '' &&
                            showErrors
                        "
                    />
                    <div
                        v-if="
                            dateOfBirthInvalidMessage !== null &&
                            dateOfBirthInvalidMessage !== '' &&
                            showErrors
                        "
                        class="text-red-500 font-medium"
                    >
                        {{ dateOfBirthInvalidMessage }}
                    </div>
                </div>

                <div v-if="showFacilitySelect">
                    <div>{{ t('signInFlow.chooseFacility') }}</div>
                    <facility-select
                        v-model="selectedFacility"
                        :facility-groups="facilityGroups"
                        :invalid="showErrors && facilityInvalidMessage !== null"
                        :disabled="isPending"
                    />
                    <div
                        v-if="facilityInvalidMessage !== null && showErrors"
                        class="text-red-500 font-medium"
                    >
                        {{ facilityInvalidMessage }}
                    </div>
                </div>
            </div>
        </div>

        <prime-button
            type="submit"
            :label="t('next')"
            class="w-full"
            :loading="isPending"
        />

        <confirm-dialog
            group="ageOfMajorityChallenge"
            :pt="{
                root: {
                    class: '!w-[400px]'
                },
                footer: {
                    class: 'space-x-4'
                },
                rejectButton: {
                    outlined: true
                }
            }"
            :pt-options="{
                mergeProps: true,
                mergeSections: true
            }"
        >
            <template #message="slotProps">
                <div
                    class="flex-column align-items-center border-bottom-1 surface-border flex w-full gap-3"
                >
                    <i
                        :class="slotProps.message.icon"
                        class="text-6xl text-primary-500"
                    ></i>
                    <i18n-t
                        keypath="signInFlow.ageOfMajorityHint"
                        tag="p"
                        class="whitespace-pre-line"
                    >
                        <template #ageOfMajorityHint>
                            {{ props.ageOfMajorityHint }}
                        </template>
                        <template #guardianWarning>
                            <span class="text-red-600 font-medium">
                                {{ t('signInFlow.guardianAgeWarning') }}
                            </span>
                        </template>
                    </i18n-t>
                </div>
            </template>
        </confirm-dialog>
    </form>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import PrimeButton from 'primevue/button'
import InputText from 'primevue/inputtext'
import { useMutation } from '@tanstack/vue-query'
import { useRpQuery } from '~/composables/graphql'
import type { FragmentType } from '~/resources/graphql'
import { graphql } from '~/resources/graphql'
import type { VerifyDateOfBirthInput } from '~/resources/graphql/graphql'
import { PossibleCustomerFragment } from '~/components/Verification/ChooseCustomer.vue'
import { useSetViewer } from '~/composables/use-login'
import { useShowAlert } from '~/composables/use-show-alert'
import { isSfrc } from '~/composables/use-org'
import DropdownDateInput from '~/components/Input/DropdownDateInput.vue'
import FacilitySelect from '~/components/Verification/FacilitySelect.vue'
import { I18nT, useI18n } from 'vue-i18n'
import { parseTimeless } from '~/composables/support/localization'
import { differenceInYears, isAfter } from 'date-fns'
import { useConfirm } from 'primevue/useconfirm'
import ConfirmDialog from 'primevue/confirmdialog'
import { useLayoutMode } from '~/composables/use-layout-mode'

const props = defineProps<{
    token: string
    orgLevel: boolean
    facilityGroups: {
        id: string
        name: string
        facilities: {
            id: string
            label: string
        }[]
    }[]
    ageOfMajorityHint: number
    onlineProfileAgeMinimum: number
}>()

const emit = defineEmits<{
    (e: 'done'): void
    (
        e: 'multiple',
        payload: FragmentType<typeof PossibleCustomerFragment>
    ): void
    (e: 'createCustomer', payload: string): void
}>()

const { t } = useI18n({ useScope: 'global' })
const { isKiosk } = useLayoutMode()
const firstNameRef = ref<HTMLDivElement>()
onMounted(() => {
    // Focus input on mounted
    ;(
        firstNameRef.value?.querySelector('#first_name') as
            | HTMLInputElement
            | null
            | undefined
    )?.focus?.()
})

const showErrors = ref(false)
const useDateOfBirth = !isSfrc()
const firstName = ref('')
const middleName = ref('')
const lastName = ref('')
const dateOfBirth = ref('')
const selectedFacility = ref('')
const showFacilitySelect = computed(() => {
    if (!props.orgLevel) {
        return false
    }

    return props.facilityGroups.flatMap(group => group.facilities).length > 1
})

const firstNameInvalidMessage = computed(() => {
    if (firstName.value.length < 1) {
        return t('customerInformation.firstNameRequired')
    }

    return null
})

const lastNameInvalidMessage = computed(() => {
    if (lastName.value.length < 2) {
        return t('customerInformation.lastNameRequired')
    }

    return null
})

const dateOfBirthInvalidMessage = computed(() => {
    // Override for SFRC
    if (!useDateOfBirth) {
        return null
    }

    if (dateOfBirth.value === '') {
        return ''
    }

    const now = new Date()
    const customerDob = parseTimeless(dateOfBirth.value)
    if (isAfter(customerDob, now)) {
        return t('purchaseContract.futureDateOfBirthWarning')
    }

    const customerAge = differenceInYears(now, customerDob)
    if (customerAge > 120) {
        return t('purchaseContract.dateOfBirthInvalid')
    }

    if (customerAge < props.onlineProfileAgeMinimum) {
        return t('purchaseContract.ageAtLeastRequired', {
            minimum: props.onlineProfileAgeMinimum
        })
    }

    return null
})

const facilityInvalidMessage = computed(() => {
    if (!showFacilitySelect.value) {
        return null
    }

    if (selectedFacility.value === '') {
        return t('signInFlow.facilityRequired')
    }

    return null
})

const showAlert = useShowAlert()
const setViewer = useSetViewer()
const confirm = useConfirm()
async function handleSubmit(event: Event) {
    // Prevent browser submitting form
    event.preventDefault()

    // Check for errors
    if (
        dateOfBirthInvalidMessage.value !== null ||
        facilityInvalidMessage.value !== null ||
        firstNameInvalidMessage.value !== null ||
        lastNameInvalidMessage.value !== null
    ) {
        showErrors.value = true
        return
    }

    const now = new Date()
    // Invalid Date of Birth
    const customerDob = parseTimeless(dateOfBirth.value)
    const customerAge = differenceInYears(now, customerDob)

    const mutationData: VerifyDateOfBirthInput = {
        token: props.token,
        dateOfBirth: dateOfBirth.value === '' ? null : dateOfBirth.value,
        firstName: firstName.value,
        middleName: middleName.value,
        lastName: lastName.value,
        facility: selectedFacility.value === '' ? null : selectedFacility.value
    }

    if (props.ageOfMajorityHint > 0 && customerAge < props.ageOfMajorityHint) {
        confirm.require({
            group: 'ageOfMajorityChallenge',
            header: t('signInFlow.confirmAge'),
            rejectClass: 'text-primary-600 bg-white',
            rejectLabel: t('continue'),
            acceptLabel: t('signInFlow.editDateOfBirth'),
            reject: () => {
                performMutation(mutationData)
            }
        })
    } else {
        performMutation(mutationData)
    }
}

const query = useRpQuery()
const { mutate, isPending } = useMutation({
    mutationFn: (input: VerifyDateOfBirthInput) =>
        query(
            graphql(/** @lang GraphQL */ `
                mutation VerifyDateOfBirth($input: VerifyDateOfBirthInput!) {
                    verifyDateOfBirth(input: $input) {
                        __typename
                        ... on VerifyDateOfBirthResult {
                            record {
                                ...ViewerFragment
                            }
                        }
                        ... on VerifyDateOfBirthCreateCustomerResult {
                            record {
                                ...ViewerFragment
                            }
                        }
                        ... on VerifyDateOfBirthMultipleResult {
                            ...PossibleCustomerFragment
                        }
                        ... on ValidationError {
                            fieldErrors {
                                message
                            }
                        }
                    }
                }
            `),
            {
                input
            }
        )
})

function performMutation(mutationData: VerifyDateOfBirthInput) {
    mutate(mutationData, {
        onSuccess: async data => {
            // This means we attached to an existing customer
            if (
                data.verifyDateOfBirth.__typename === 'VerifyDateOfBirthResult'
            ) {
                // Kiosk users should land directly on the next document or purchase step.
                if (!isKiosk.value) {
                    await showAlert(
                        t('signInFlow.accountLinkedTitle'),
                        t('signInFlow.accountLinkedText'),
                        true,
                        t('continue')
                    )
                }
                // TODO: Check customer name is filled out
                setViewer(data.verifyDateOfBirth.record)
                emit('done')
                return
            }
            // This means we made a new customer
            else if (
                data.verifyDateOfBirth.__typename ===
                'VerifyDateOfBirthCreateCustomerResult'
            ) {
                // Kiosk users should not see a profile-complete stop before continuing.
                if (!isKiosk.value) {
                    await showAlert(
                        t('signInFlow.accountCreatedTitle'),
                        t('signInFlow.accountCreatedText'),
                        true,
                        t('continue')
                    )
                }
                setViewer(data.verifyDateOfBirth.record)
                emit('done')
                return
            }
            // This means we have multiple customers with the same name, so need to pick one
            else if (
                data.verifyDateOfBirth.__typename ===
                'VerifyDateOfBirthMultipleResult'
            ) {
                emit('multiple', data.verifyDateOfBirth)
                return
            }

            // Unknown result
            showAlert(t('oops'), t('somethingWrongWarning'))
        }
    })
}
</script>
